import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  RefreshCw,
  CheckCircle,
  Database,
  Filter,
  Download,
  Search
} from "lucide-react";
import { 
  HistoricalMaterialPrice
} from "@/types";

const HistoricalPrices = () => {
  const [selectedQuarter, setSelectedQuarter] = useState("2024-Q1");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedGeoZone, setSelectedGeoZone] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const [historicalPrices] = useState<HistoricalMaterialPrice[]>([
    {
      id: "1",
      materialId: "CEM-001",
      geoZoneId: "LAG",
      quarter: "2024-Q1",
      year: 2024,
      quarterNumber: 1,
      averagePrice: 5800.00,
      minPrice: 5600.00,
      maxPrice: 6000.00,
      medianPrice: 5800.00,
      standardDeviation: 160.00,
      priceChangePercentage: 5.45,
      dataPointsCount: 65,
      currency: "NGN",
      source: "manual",
      confidenceScore: 0.85,
      notes: "Q1 2024 new year pricing",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "2",
      materialId: "CEM-001",
      geoZoneId: "KAN",
      quarter: "2024-Q1",
      year: 2024,
      quarterNumber: 1,
      averagePrice: 4800.00,
      minPrice: 4600.00,
      maxPrice: 5000.00,
      medianPrice: 4800.00,
      standardDeviation: 140.00,
      priceChangePercentage: 5.49,
      dataPointsCount: 50,
      currency: "NGN",
      source: "manual",
      confidenceScore: 0.85,
      notes: "Q1 2024 new year pricing",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "3",
      materialId: "STEEL-001",
      geoZoneId: "LAG",
      quarter: "2024-Q1",
      year: 2024,
      quarterNumber: 1,
      averagePrice: 420000.00,
      minPrice: 400000.00,
      maxPrice: 450000.00,
      medianPrice: 420000.00,
      standardDeviation: 15000.00,
      priceChangePercentage: 3.25,
      dataPointsCount: 45,
      currency: "NGN",
      source: "api",
      confidenceScore: 0.92,
      notes: "Steel prices from Dangote API",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "4",
      materialId: "STEEL-001",
      geoZoneId: "ABJ",
      quarter: "2024-Q1",
      year: 2024,
      quarterNumber: 1,
      averagePrice: 435000.00,
      minPrice: 415000.00,
      maxPrice: 460000.00,
      medianPrice: 435000.00,
      standardDeviation: 18000.00,
      priceChangePercentage: 2.85,
      dataPointsCount: 38,
      currency: "NGN",
      source: "api",
      confidenceScore: 0.88,
      notes: "Steel prices from Dangote API",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    }
  ]);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'decreasing':
        return <TrendingDown className="h-4 w-4 text-red-600" />;
      default:
        return <Minus className="h-4 w-4 text-gray-600" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getMaterialName = (materialId: string) => {
    const materialNames: { [key: string]: string } = {
      'CEM-001': 'Cement (Grade 42.5)',
      'STEEL-001': 'Steel Reinforcement (12mm)',
      'AGG-001': 'Granite Aggregate (20mm)',
      'BLK-001': 'Concrete Block (9 inch)'
    };
    return materialNames[materialId] || materialId;
  };

  const filteredPrices = historicalPrices.filter(price => {
    const matchesQuarter = selectedQuarter === "all" || price.quarter === selectedQuarter;
    const matchesCategory = selectedCategory === "all" || 
      (selectedCategory === "Cement" && price.materialId.includes("CEM")) ||
      (selectedCategory === "Steel" && price.materialId.includes("STEEL")) ||
      (selectedCategory === "Aggregates" && price.materialId.includes("AGG")) ||
      (selectedCategory === "Blocks" && price.materialId.includes("BLK"));
    const matchesGeoZone = selectedGeoZone === "all" || price.geoZoneId === selectedGeoZone;
    const matchesSearch = searchTerm === "" || 
      getMaterialName(price.materialId).toLowerCase().includes(searchTerm.toLowerCase()) ||
      price.geoZoneId.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesQuarter && matchesCategory && matchesGeoZone && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Historical Material Prices</h2>
          <p className="text-gray-600">Quarterly price history with trend analysis</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Data
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filters & Search
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <Label htmlFor="quarter">Quarter</Label>
              <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Quarters</SelectItem>
                  <SelectItem value="2024-Q1">2024 Q1</SelectItem>
                  <SelectItem value="2023-Q4">2023 Q4</SelectItem>
                  <SelectItem value="2023-Q3">2023 Q3</SelectItem>
                  <SelectItem value="2023-Q2">2023 Q2</SelectItem>
                  <SelectItem value="2023-Q1">2023 Q1</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="category">Material Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Cement">Cement</SelectItem>
                  <SelectItem value="Steel">Steel</SelectItem>
                  <SelectItem value="Aggregates">Aggregates</SelectItem>
                  <SelectItem value="Blocks">Blocks</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="geoZone">Geo Zone</Label>
              <Select value={selectedGeoZone} onValueChange={setSelectedGeoZone}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Zones</SelectItem>
                  <SelectItem value="LAG">Lagos</SelectItem>
                  <SelectItem value="ABJ">Abuja</SelectItem>
                  <SelectItem value="KAN">Kano</SelectItem>
                  <SelectItem value="PHC">Port Harcourt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  placeholder="Search materials..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="action">Action</Label>
              <Button className="w-full" size="sm">
                <Database className="h-4 w-4 mr-2" />
                Add Price
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Materials</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredPrices.length}</div>
            <p className="text-xs text-muted-foreground">Price entries</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Change</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              +{filteredPrices.length > 0 ? 
                (filteredPrices.reduce((sum, price) => sum + (price.priceChangePercentage || 0), 0) / filteredPrices.length).toFixed(1) 
                : 0}%
            </div>
            <p className="text-xs text-muted-foreground">vs last quarter</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Confidence</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {filteredPrices.filter(p => p.confidenceScore > 0.8).length}
            </div>
            <p className="text-xs text-muted-foreground">reliable data</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Data Points</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredPrices.reduce((sum, price) => sum + price.dataPointsCount, 0)}
            </div>
            <p className="text-xs text-muted-foreground">total samples</p>
          </CardContent>
        </Card>
      </div>

      {/* Historical Prices Table */}
      <Card>
        <CardHeader>
          <CardTitle>Historical Material Prices</CardTitle>
          <CardDescription>Quarterly price history with trend analysis</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Material</TableHead>
                <TableHead>Geo Zone</TableHead>
                <TableHead>Quarter</TableHead>
                <TableHead>Average Price</TableHead>
                <TableHead>Price Range</TableHead>
                <TableHead>Change %</TableHead>
                <TableHead>Data Points</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead>Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPrices.map((price) => (
                <TableRow key={price.id}>
                  <TableCell className="font-medium">{getMaterialName(price.materialId)}</TableCell>
                  <TableCell>{price.geoZoneId}</TableCell>
                  <TableCell>{price.quarter}</TableCell>
                  <TableCell className="font-medium">{formatCurrency(price.averagePrice)}</TableCell>
                  <TableCell>
                    {price.minPrice && price.maxPrice && 
                      `${formatCurrency(price.minPrice)} - ${formatCurrency(price.maxPrice)}`
                    }
                  </TableCell>
                  <TableCell className="flex items-center">
                    {getTrendIcon(price.priceChangePercentage && price.priceChangePercentage > 0 ? 'increasing' : 'decreasing')}
                    <span className={price.priceChangePercentage && price.priceChangePercentage > 0 ? 'text-green-600' : 'text-red-600'}>
                      {price.priceChangePercentage?.toFixed(2)}%
                    </span>
                  </TableCell>
                  <TableCell>{price.dataPointsCount}</TableCell>
                  <TableCell>
                    <Badge variant={price.confidenceScore > 0.8 ? 'default' : 'secondary'}>
                      {(price.confidenceScore * 100).toFixed(0)}%
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={price.source === 'api' ? 'default' : 'outline'}>
                      {price.source}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoricalPrices;

